import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CreditCard, Wallet, Banknote, Check, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import PayPalCheckout from '@/components/PayPalCheckout';

const METHODS = [
  { id: 'stripe', icon: CreditCard, key: 'checkout.stripe', sub: 'Visa · Mastercard · Amex · Apple Pay · Google Pay' },
  { id: 'klarna', icon: CalendarClock, key: 'checkout.klarna', sub: 'Klarna — Pay in 4 interest-free installments' },
  { id: 'afterpay', icon: CalendarClock, key: 'checkout.afterpay', sub: 'Afterpay / Clearpay — Pay every 2 weeks, no interest' },
  { id: 'paypal', icon: Wallet, key: 'checkout.paypal', sub: 'Pay with PayPal balance, bank, or card' },
  { id: 'manual', icon: Banknote, key: 'checkout.manual', sub: 'Zelle · Venmo · CashApp — instructions sent after' },
];

export default function Checkout() {
  const { t, cart, api, user, clearCart } = useApp();
  const [method, setMethod] = useState('stripe');
  const [manualMethod, setManualMethod] = useState('zelle');
  const [contact, setContact] = useState({ name: user?.name || '', email: user?.email || '' });
  const [loading, setLoading] = useState(false);
  const [manualResult, setManualResult] = useState(null);
  const navigate = useNavigate();
  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  if (cart.length === 0 && !manualResult) return (
    <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="checkout-empty">
      <h1 className="font-display text-4xl">Your bag is empty</h1>
    </div>
  );

  const startStripe = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/checkout/stripe', {
        items: cart.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
        payment_method: method,  // stripe | klarna | afterpay — all route through Stripe Checkout
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
    }
  };

  const startManual = async () => {
    if (!contact.name || !contact.email) { toast.error('Please provide contact info'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/checkout/manual', {
        items: cart.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        method: manualMethod,
        contact_email: contact.email,
        contact_name: contact.name,
      });
      setManualResult(data);
      clearCart();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  };

  const startPayPal = () => {
    toast.info('PayPal is in test mode — please use card or manual transfer for now.');
  };

  const onPayPalSuccess = () => {
    clearCart();
    navigate('/account');
  };

  if (manualResult) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="manual-success">
        <Check size={48} strokeWidth={1} className="mx-auto text-accent" />
        <h1 className="font-display text-4xl mt-8">Order Received</h1>
        <p className="mt-4 font-light text-muted-foreground">Send <strong>${manualResult.amount.toLocaleString()}</strong> using {manualResult.instructions?.label} and reference code <strong className="tracking-widest">{manualResult.reference}</strong>. We will confirm your order once payment is received.</p>
        <button onClick={() => navigate('/account')} className="mt-10 inline-block bg-foreground text-background px-8 py-4 overline" data-testid="manual-view-orders">View My Orders</button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16" data-testid="checkout-page">
      <h1 className="font-display text-4xl sm:text-5xl mb-12">{t('checkout.title')}</h1>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-10">
          <section>
            <div className="overline mb-4">{t('checkout.contact')}</div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <Label className="overline">{t('auth.name')}</Label>
                <Input className="mt-2 rounded-none border-0 border-b border-foreground focus-visible:ring-0" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="checkout-name" />
              </div>
              <div>
                <Label className="overline">{t('auth.email')}</Label>
                <Input className="mt-2 rounded-none border-0 border-b border-foreground focus-visible:ring-0" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="checkout-email" />
              </div>
            </div>
          </section>

          <section>
            <div className="overline mb-4">{t('checkout.method')}</div>
            <div className="space-y-3">
              {METHODS.map((m) => (
                <button key={m.id} onClick={() => setMethod(m.id)} className={`w-full text-left p-5 border ${method === m.id ? 'border-foreground' : 'border-border'} flex items-start gap-4 hover:border-foreground transition`} data-testid={`pm-${m.id}`}>
                  <m.icon size={20} strokeWidth={1.3} className="mt-1" />
                  <div className="flex-1">
                    <div className="font-display text-lg">{t(m.key)}</div>
                    <div className="text-xs text-muted-foreground font-light mt-1">{m.sub}</div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border ${method === m.id ? 'bg-foreground border-foreground' : 'border-border'}`} />
                </button>
              ))}
            </div>

            {method === 'manual' && (
              <div className="mt-6 p-6 bg-secondary">
                <div className="overline mb-4">Select Transfer Service</div>
                <div className="flex gap-3 flex-wrap">
                  {[{id:'zelle',label:'Zelle'},{id:'venmo',label:'Venmo'},{id:'cashapp',label:'CashApp'}].map((m) => (
                    <button key={m.id} onClick={() => setManualMethod(m.id)} className={`px-5 py-3 border ${manualMethod === m.id ? 'border-foreground bg-foreground text-background' : 'border-border'} overline`} data-testid={`mp-${m.id}`}>{m.label}</button>
                  ))}
                </div>
                <p className="mt-4 text-xs font-light text-muted-foreground">After placing your order we will send detailed payment instructions and a reference code to your email.</p>
              </div>
            )}
          </section>
        </div>

        <div className="lg:col-span-5">
          <div className="border border-border p-8 sticky top-32" data-testid="checkout-summary">
            <div className="overline mb-6">Order Summary</div>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {cart.map((i) => (
                <div key={i.id} className="flex gap-3">
                  <div className="w-14 aspect-[4/5] bg-muted overflow-hidden flex-shrink-0">
                    {i.image && <img src={i.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 text-sm font-light">
                    <div className="font-display">{i.title}</div>
                    <div className="text-muted-foreground text-xs">{i.designer} · Qty {i.quantity}</div>
                  </div>
                  <div className="text-sm">${(i.price * i.quantity).toLocaleString()}</div>
                </div>
              ))}
            </div>
            <div className="border-t border-border my-6"></div>
            <div className="flex justify-between font-light text-sm"><span>{t('cart.subtotal')}</span><span>${subtotal.toLocaleString()}</span></div>
            <div className="text-xs text-muted-foreground font-light mt-1">{t('cart.shipping')}</div>
            <div className="flex justify-between font-display text-xl mt-6"><span>{t('cart.total')}</span><span>${subtotal.toLocaleString()}</span></div>

            {(method === 'stripe' || method === 'klarna' || method === 'afterpay') && (
              <button disabled={loading} onClick={startStripe} className="mt-8 w-full bg-foreground text-background px-8 py-4 overline disabled:opacity-50" data-testid={`pay-${method}`}>{loading ? t('checkout.processing') : `${t('cta.checkout')} — ${method === 'klarna' ? 'Klarna' : method === 'afterpay' ? 'Afterpay' : t('checkout.stripe')}`}</button>
            )}
            {method === 'paypal' && (
              <PayPalCheckout items={cart} amount={subtotal} onSuccess={onPayPalSuccess} />
            )}
            {method === 'manual' && (
              <button disabled={loading} onClick={startManual} className="mt-8 w-full bg-foreground text-background px-8 py-4 overline disabled:opacity-50" data-testid="pay-manual">{loading ? '…' : `Place Order — ${manualMethod.toUpperCase()}`}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
